/* eslint-disable max-len */
import type { CaseDetail, CaseDetailAddDTO, CaseFolder, CaseFolderObject, SiteModel, UploadInitResponse } from '../models/DataAdminTypes';
import type IDataAdminService from './IDataAdminService';
import { getAppConfig } from '../utils/appConfig';
import { CompletedPart, PresignedPostData } from './ClientMultipartUploader';
import { logEvent, logError } from '../utils/trackingUtils';
import { call } from '../utils/apiUtils';

export default class DataAdminService implements IDataAdminService {
  siteCache: (SiteModel[] | null) = null;
  config = getAppConfig();

  clearCache(): void {
    this.siteCache = null;
  }

  async createCaseDetail(accessToken: string, siteCode: string, caseDetail: CaseDetailAddDTO): Promise<CaseDetail> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/cases/${siteCode}/case-details`;
    try {
      response = await call(url, accessToken, 'POST', caseDetail);
      logEvent({ action: 'service.case_detail.create.success', siteCode, caseDetail });
    } catch (error: unknown) {
      logError('service.case_detail.create.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error creating case detail at [${url}]:`, error);
      throw new Error(message || 'Case detail creation error.');
    }
    const apiCase = await response.json();
    return {
      caseId: apiCase.case_id,
      caseDate: apiCase.case_date,
      caseFolder: apiCase.case_folder,
      dataBucket: apiCase.data_bucket,
      isRevision: apiCase.is_revision,
      isNavigated: apiCase.is_navigated,
      isStaged: apiCase.is_staged,
      createdBy: apiCase.created_by,
      updatedBy: apiCase.updated_by,
      createdAt: new Date(apiCase.created_at),
      updatedAt: new Date(apiCase.updated_at),
    } as CaseDetail;
  }

  async createCaseFolder(accessToken: string, siteCode: string, caseId: string, startDate: string): Promise<string> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/cases/${siteCode}/case-folders`;
    try {
      response = await call(url, accessToken, 'POST', { case_key: caseId, case_start_date: startDate });
      logEvent({ action: 'service.case_folder.create.success', siteCode, caseId, startDate });
    } catch (error: unknown) {
      logError('service.case_folder.create.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error creating case folder at [${url}]:`, error);
      throw new Error(message || 'Case folders creation error.');
    }
    return (await response.json()).folder_name;
  }

  async createSubFolder(
    accessToken: string,
    siteCode: string,
    caseFolder: string,
    subFolderPath: string
  ): Promise<string> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/cases/${siteCode}/case-folders/${caseFolder}/subfolder`;
    try {
      response = await call(url, accessToken, 'POST', { subfolder_path: subFolderPath });
      logEvent({ action: 'service.subfolder.create.success', siteCode, caseFolder, subFolderPath });
    } catch (error: unknown) {
      logError('service.subfolder.create.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error creating subfolder at [${url}]:`, error);
      throw new Error(message || 'Subfolder creation error.');
    }
    return (await response.json()).folder_name;
  }

  async getServiceHealth(): Promise<object> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/admin/dependencies`;
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      logEvent({ action: 'service.health.get.success' });
    } catch (error) {
      logError('service.health.get.error', error);
      console.error(`Error fetching service health from [${url}]:`, error);
      throw new Error('Service health fetch error.');
    }

    let healthStatus;
    try {
      healthStatus = await response.json();
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Service health parse error.');
    }
    return healthStatus;
  }

  async getSites(accessToken: string): Promise<SiteModel[]> {
    if (this.siteCache) {
      return this.siteCache;
    }

    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/sites`;
    try {
      response = await call(url, accessToken, 'GET');
      logEvent({ action: 'service.sites.get.success' });
    } catch (error: unknown) {
      logError('service.sites.get.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching sites from [${url}]:`, error);
      throw new Error(message || 'Sites fetch error.');
    }

    let sites;
    try {
      sites = (await response.json()).map((site: { site_id: string, site_code: string, site_name: string }) => ({
        siteId: site.site_id,
        siteCode: site.site_code,
        siteName: site.site_name
      }));
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Sites parse error.');
    }

    this.siteCache = sites;
    return sites;
  }

  /**
   * Retrieve case summary data.
   *
   * @param accessToken The access token to use for authentication.
   * @returns
   */
  async getCaseFoldersForSite(accessToken: string, siteCode: string): Promise<CaseFolder[]> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/cases/${siteCode}/case-folders`;
    try {
      response = await call(url, accessToken, 'GET');
      logEvent({ action: 'service.case_folders.get.success', siteCode });
    } catch (error: unknown) {
      logError('service.case_folders.get.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching case summaries from [${url}]:`, error);
      throw new Error(message || 'Case folders fetch error.');
    }

    let caseFolders;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      caseFolders = (await response.json()).map((caseFolder: any) => ({
        siteCode: caseFolder.site_code,
        folderName: caseFolder.folder_name,
        lastModifiedDate: caseFolder.last_modified_date
      }));
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Case folders parse error.');
    }
    return caseFolders;
  }

  async getCaseDetailsForSite(accessToken: string, siteCode: string, onlyStaged: boolean = false): Promise<CaseDetail[]> {
    const baseUrl = `${this.config.DATA_ADMIN_API_URL}/api/v1/cases/${siteCode}/case-details`;
    const url = onlyStaged ? `${baseUrl}?staged=true` : baseUrl;

    let response: Response;

    try {
      response = await call(url, accessToken, 'GET');
      logEvent({ action: 'service.case_details.get.success', siteCode, onlyStaged });
    } catch (error: unknown) {
      logError('service.case_details.get.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching case details from [${url}]:`, error);
      throw new Error(message || 'Case details fetch error.');
    }

    try {
      const data = await response.json();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.map((caseDetail: any) => ({
        caseId: caseDetail.case_id,
        caseDate: caseDetail.case_date,
        siteId: caseDetail.site_uuid,
        caseFolder: caseDetail.case_folder,
        dataBucket: caseDetail.data_bucket,
        isRevision: caseDetail.is_revision,
        isNavigated: caseDetail.is_navigated,
        isStaged: caseDetail.is_staged,
        createdBy: caseDetail.created_by,
        updatedBy: caseDetail.updated_by,
        createdAt: new Date(caseDetail.created_at),
        updatedAt: new Date(caseDetail.updated_at)
      }));
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Case details parse error.');
    }
  }

  async getFolderObjects(accessToken: string, siteCode: string, caseFolder: string): Promise<CaseFolderObject[]> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/cases/${siteCode}/case-folders/${caseFolder}/files`;
    try {
      response = await call(url, accessToken, 'GET');
      logEvent({ action: 'service.folder_objects.get.success', siteCode, caseFolder });
    } catch (error: unknown) {
      logError('service.folder_objects.get.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error fetching folder objects from [${url}]:`, error);
      throw new Error(message || 'Folder objects fetch error.');
    }

    let folderObjects: CaseFolderObject[] = [];
    try {
      const apiObjects = await response.json();
      folderObjects = apiObjects.map(
        (
          obj: { object_key: string, last_modified: Date, size: number, size_desc: string }
        ) => ({
          objectKey: obj.object_key,
          filePathSansCaseFolder: obj.object_key.replace(`${caseFolder}/`, ''),
          isFolder: obj.object_key.endsWith('/') && obj.size === 0,
          isSubFolder: false,
          lastModified: obj.last_modified,
          size: obj.size,
          sizeDesc: obj.size_desc
        } as CaseFolderObject));
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Folder objects parse error.');
    }
    return folderObjects;
  }

  async initFileUpload(
    accessToken: string,
    siteCode: string,
    caseFolder: string,
    key: string,
    fileSizeBytes: number
  ): Promise<UploadInitResponse> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/case-files/${siteCode}/case-folders/${caseFolder}/upload-init` +
      `?object_key=${encodeURIComponent(key)}&file_size_bytes=${fileSizeBytes}`;
    try {
      response = await call(url, accessToken, 'GET');
      logEvent({ action: 'service.file_upload.init.success', siteCode, caseFolder, key, fileSizeBytes });
    } catch (error: unknown) {
      logError('service.file_upload.init.error', error);
      const message = error instanceof Error ? error.message : String(error);
      console.error(`Error initiating file upload at [${url}]:`, error);
      throw new Error('Error initiating file upload: ' + message);
    }

    let responseData;
    try {
      const rawResponse = await response.json();
      responseData = {
        partSize: rawResponse.partSize,
        uploadId: rawResponse.uploadId,
        objectKey: rawResponse.key,
      };
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Error calling service to initiate file upload.');
    }
    return responseData;
  }

  async getMultiPartPresignedUrl(
    accessToken: string,
    siteCode: string,
    caseFolder: string,
    partNumber: number,
    path: string,
    uploadId: string,
    checksum: string
  ): Promise<string> {
    let response: Response;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/case-files/${siteCode}/case-folders/${caseFolder}/upload-part-url` +
      `?upload_id=${encodeURIComponent(uploadId)}&object_key=${encodeURIComponent(path)}&part_number=${partNumber}&checksum=${encodeURIComponent(checksum)}`;

    try {
      response = await call(url, accessToken, 'GET');
      logEvent({ action: 'service.file_upload.part_url.success', siteCode, caseFolder, partNumber, path, uploadId, checksum });
    } catch (error) {
      logError('service.file_upload.part_url.error', error);
      const errorMessage = error instanceof Error ?
        error.message :
        String(error);
      console.error(`Error retrieving upload POST from [${url}]:`, error);

      throw new Error('Error processing file upload: ' + errorMessage);
    }

    let signedUrl: string = '';
    try {
      const rawResponse = await response.json();
      if (!rawResponse || !rawResponse.length) {
        throw new Error('No presigned URL returned from server.');
      }
      signedUrl = rawResponse;
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Error forming the upload request.');
    }
    return signedUrl;
  }

  /**
   * Complete a multipart upload by notifying the server that all parts have been uploaded.
   */
  async completeMultipartUpload(
    accessToken: string,
    siteCode: string,
    caseFolder: string,
    objectKey: string,
    uploadId: string,
    completedParts: CompletedPart[]
  ): Promise<PresignedPostData> {
    let response: Response = new Response();
    let lastErrorMessage: string | null = null;
    const url = `${this.config.DATA_ADMIN_API_URL}/api/v1/case-files/${siteCode}/case-folders/${caseFolder}/complete-upload` +
      `?upload_id=${uploadId}&object_key=${encodeURIComponent(objectKey)}`;

    // Retry request on failure
    const RETRY_COUNT = 4;
    let attempt = 0;
    do {
      try {
        response = await call(url, accessToken, 'POST', completedParts);
        logEvent({ action: 'service.file_upload.complete.success', siteCode, caseFolder, objectKey, uploadId, completedParts });
        break; // Exit loop if successful
      } catch (error: unknown) {
        logError('service.file_upload.complete.error', error);
        console.error(`Error calling API to complete upload at [${url}]:`, error);
        const message = error instanceof Error ? error.message : String(error);
        lastErrorMessage = message;

        // Exponential backoff
        if (attempt < RETRY_COUNT - 1) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        } else {
          throw new Error(lastErrorMessage || 'Error processing file upload completion.');
        }
      }
    } while (++attempt < RETRY_COUNT);

    let responseData;
    try {
      responseData = await response.json();
    } catch (error) {
      console.error(`Error parsing JSON response from [${url}]:`, error);
      throw new Error('Error completing the upload request.');
    }
    return responseData;
  }

}
